/**
 * PageHeader — the top of every screen: an optional eyebrow (a back link / breadcrumb),
 * the h1 with inline badges, a one-line description, and right-aligned actions. Pair
 * with `.argus-page` for the standard vertical rhythm.
 *
 * Router-free: `backTo` is a SLOT — any node, typically a link carrying a leading
 * `ChevronLeft` and its label — rendered first in the eyebrow row. apps/web's PageHeader
 * wraps this one and fills the slot with a typed TanStack `<Link>` built from its
 * `backTo: { to, params, label }` prop, so no screen there changed.
 */
import type { ReactNode } from 'react';

export interface PageHeaderProps {
  title: ReactNode;
  /** Badges or a status pill rendered inline after the title. */
  titleAddon?: ReactNode | undefined;
  description?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  /** A "‹ Back to …" link above the title (the app supplies the link element). */
  backTo?: ReactNode | undefined;
  /** Free-form eyebrow (e.g. a case number) when `backTo` is not enough. */
  eyebrow?: ReactNode | undefined;
}

export function PageHeader({
  title,
  titleAddon,
  description,
  actions,
  backTo,
  eyebrow,
}: PageHeaderProps): JSX.Element {
  return (
    <header className="argus-page-header">
      <div className="argus-page-header__main">
        {backTo || eyebrow ? (
          <div className="argus-page-header__eyebrow">
            {backTo}
            {backTo && eyebrow ? <span aria-hidden="true">·</span> : null}
            {eyebrow}
          </div>
        ) : null}
        <div className="argus-page-header__title">
          <h1>{title}</h1>
          {titleAddon}
        </div>
        {description ? <p className="argus-page-header__description">{description}</p> : null}
      </div>
      {actions ? <div className="argus-page-header__actions">{actions}</div> : null}
    </header>
  );
}
